/**
 * Unified EXIF Metadata Utilities
 *
 * Consolidates all EXIF formatting logic into a single helper module.
 * Handles multiple EXIF data structures and provides consistent formatting.
 *
 * @module utils/exif
 */

import type { PhotoExifMetadata } from '@/types/exif';
import type { ExifData } from '@/types/histogram';

// ============================================================================
// Individual Field Formatters
// ============================================================================

/**
 * Format ISO value from EXIF metadata
 * @param exif - PhotoExifMetadata object
 * @returns Formatted string like "ISO 800" or null if missing
 */
export function formatISO(exif: PhotoExifMetadata | null): string | null {
  const iso = exif?.exposure?.iso;
  if (iso === undefined || iso === null) return null;
  return `ISO ${iso}`;
}

/**
 * Format focal length from EXIF metadata
 * @param exif - PhotoExifMetadata object
 * @returns Formatted string like "50mm" or null if missing
 */
export function formatFocalLength(exif: PhotoExifMetadata | null): string | null {
  const fl = exif?.focus?.focalLength;
  if (fl === undefined || fl === null) return null;
  // Runtime defensive check for backward compatibility with untyped data
  if (typeof fl === 'string') {
    const flStr = fl as string;
    // Zoom lenses like "24-70mm" - pass through unchanged
    if (flStr.includes('-')) return flStr;
    // Already in new format with space - pass through
    if (flStr.endsWith(' mm')) return flStr;
    // Old format "50mm" - reformat to "50 mm"
    if (flStr.endsWith('mm')) return flStr.replace(/mm$/, ' mm');
    // Plain number string like "50" - add " mm"
    return `${flStr} mm`;
  }
  return `${fl} mm`;
}

/**
 * Format aperture/f-number from EXIF metadata
 * @param exif - PhotoExifMetadata object
 * @returns Formatted string like "f/2.8" or null if missing
 */
export function formatAperture(exif: PhotoExifMetadata | null): string | null {
  const f = exif?.exposure?.fNumber;
  if (f === undefined || f === null) return null;
  // Runtime defensive check for backward compatibility with untyped data
  if (typeof f === 'string') {
    const fStr = f as string;
    // Already in new format with spaces - pass through
    if (fStr.startsWith('f / ')) return fStr;
    // Old format "f/2.8" - reformat to "f / 2.8"
    if (fStr.startsWith('f/')) return fStr.replace(/^f\//, 'f / ');
    // Plain number string like "2.8" - add "f / "
    return `f / ${fStr}`;
  }
  return `f / ${f}`;
}

/**
 * Format shutter speed/exposure time from EXIF metadata
 * @param exif - PhotoExifMetadata object
 * @returns Formatted string like "1/250s" or null if missing
 */
export function formatShutterSpeed(exif: PhotoExifMetadata | null): string | null {
  const time = exif?.exposure?.exposureTime;
  if (time === undefined || time === null) return null;
  if (typeof time === 'string') {
    // Already in new format with " sec" - pass through
    if (time.endsWith(' sec')) return time;
    // Old format with "s" suffix - reformat to " sec"
    if (time.endsWith('s')) return time.replace(/s$/, ' sec');
    // No suffix (e.g., "1/250") - add " sec"
    return `${time} sec`;
  }
  // Convert decimal to fraction (e.g., 0.004 → 1/250 sec)
  if (time >= 1) {
    return `${time} sec`;
  }
  const fraction = Math.round(1 / time);
  return `1/${fraction} sec`;
}

/**
 * Format capture date/time from EXIF metadata
 * @param exif - PhotoExifMetadata object
 * @returns Formatted date string or null if missing
 */
export function formatCaptureDateTime(exif: PhotoExifMetadata | null): string | null {
  const date = exif?.capture?.dateTimeOriginal;
  if (!date) return null;
  try {
    const d = new Date(date);
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return date; // Return original if parsing fails
  }
}

/**
 * Format GPS coordinates from EXIF metadata
 * @param exif - PhotoExifMetadata object
 * @returns Formatted string like "37.7749°N, 122.4194°W" or null if missing
 */
export function formatLocation(exif: PhotoExifMetadata | null): string | null {
  const lat = exif?.location?.latitude;
  const lon = exif?.location?.longitude;
  if (lat === undefined || lon === undefined) return null;
  const latDir = lat >= 0 ? 'N' : 'S';
  const lonDir = lon >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(4)}°${latDir}, ${Math.abs(lon).toFixed(4)}°${lonDir}`;
}

// ============================================================================
// Camera/Lens Formatters (Consolidated from multiple sources)
// ============================================================================

/**
 * Remove redundant camera manufacturer prefix from lens name
 * Uses case-insensitive character-by-character matching with word boundary trimming
 *
 * @param lensName - Full lens name
 * @param cameraName - Camera name to extract prefix from
 * @returns Lens name with redundant prefix removed
 *
 * @example
 * removeCameraPrefix("LEICA CAMERA AG SUMMILUX 1:1.7/28", "Leica Camera AG Leica Q3")
 * // Returns: "SUMMILUX 1:1.7/28"
 *
 * removeCameraPrefix("Canon EF 24-70mm f/2.8L II USM", "Canon EOS R5")
 * // Returns: "EF 24-70mm f/2.8L II USM"
 */
function removeCameraPrefix(lensName: string, cameraName: string): string {
  const cameraLower = cameraName.toLowerCase();
  const lensLower = lensName.toLowerCase();

  // Find longest character-by-character match
  let i = 0;
  while (i < Math.min(cameraLower.length, lensLower.length) && cameraLower[i] === lensLower[i]) {
    i++;
  }

  // Snap back to last complete word (last space + 1)
  const lastSpace = lensLower.lastIndexOf(' ', i - 1);
  const cutPoint = lastSpace > 0 ? lastSpace + 1 : 0;

  return cutPoint > 0 ? lensName.substring(cutPoint).trim() : lensName.trim();
}

/**
 * Format camera information from EXIF metadata
 * Handles string, CameraInfo object, or undefined
 * Prefers Lensfun-normalized displayName when available
 *
 * @param exif - PhotoExifMetadata object
 * @returns Formatted camera string or null if missing
 *
 * @example
 * formatCamera({camera: "Canon EOS R5"}) // "Canon EOS R5"
 * formatCamera({camera: {make: "Canon", model: "EOS R5"}}) // "Canon EOS R5"
 * formatCamera({camera: {displayName: "Canon EOS R5"}}) // "Canon EOS R5" (Lensfun)
 * formatCamera({camera: {make: "Canon"}}) // "Canon"
 * formatCamera(null) // null
 */
export function formatCamera(exif: PhotoExifMetadata | null): string | null {
  const camera = exif?.camera;
  if (!camera) return null;
  if (typeof camera === 'string') return camera;

  // Prefer Lensfun displayName (beautified by backend)
  if (camera.displayName) {
    return camera.displayName;
  }

  // Fallback: construct from make + model (original behavior)
  const parts: string[] = [];
  if (camera.make) parts.push(camera.make);
  if (camera.model) parts.push(camera.model);
  return parts.length > 0 ? parts.join(' ') : null;
}

/**
 * Format lens information from EXIF metadata
 * Handles string, LensInfo object, or undefined
 * Prefers Lensfun-normalized displayName when available
 * Automatically removes redundant camera manufacturer prefix
 *
 * @param exif - PhotoExifMetadata object
 * @param removePrefix - boolean indicating whether to remove the manufacturer if it is the same as the camera
 * @returns Formatted lens string or null if missing
 *
 * @example
 * formatLens({lens: "RF 24-70mm f/2.8L IS USM"}) // "RF 24-70mm f/2.8L IS USM"
 * formatLens({lens: {model: "RF 24-70mm f/2.8L IS USM"}}) // "RF 24-70mm f/2.8L IS USM"
 * formatLens({lens: {displayName: "Canon RF 24-70mm f/2.8 L IS USM"}}) // "Canon RF 24-70mm f/2.8 L IS USM" (Lensfun)
 * formatLens({camera: "Canon EOS R5", lens: "Canon EF 24-70mm f/2.8L II USM"}) // "EF 24-70mm f/2.8L II USM" (prefix removed)
 * formatLens({lens: {make: "Canon"}}) // "Canon"
 * formatLens(null) // null
 */
export function formatLens(exif: PhotoExifMetadata | null, removePrefix: boolean | null): string | null {
  const lens = exif?.lens;
  if (!lens) return null;

  let lensName: string;

  if (typeof lens === 'string') {
    lensName = lens;
  } else {
    // Prefer Lensfun displayName (beautified by backend)
    if (lens.displayName) {
      lensName = lens.displayName;
    } else {
      // Fallback: construct from make + model (original behavior)
      // Prefer model field (more specific), but fall back to make
      const parts: string[] = [];
      if (lens.make) parts.push(lens.make);
      if (lens.model) parts.push(lens.model);
      lensName = parts.length > 0 ? parts.join(' ') : '';
    }
  }

  if (!lensName) return null;

  // Remove redundant camera prefix if camera is present in exif
  const camera = formatCamera(exif);
  if (camera && removePrefix) {
    lensName = removeCameraPrefix(lensName, camera);
  }

  return lensName;
}

// ============================================================================
// Compound Formatters
// ============================================================================

/**
 * Format camera and lens information together
 * Automatically removes redundant camera manufacturer prefix from lens name
 * Example output: "Leica Camera AG Leica Q3 • SUMMILUX 1:1.7/28 ASP"
 *
 * @param exif - PhotoExifMetadata object
 * @returns Formatted string or null if both camera and lens are missing
 */
export function formatCameraAndLens(exif: PhotoExifMetadata | null): string | null {
  if (!exif) return null;

  const parts: string[] = [];

  const camera = formatCamera(exif);
  if (camera) parts.push(camera);

  const lens = formatLens(exif, true);
  if (lens) parts.push(lens);

  return parts.length > 0 ? parts.join(' • ') : null;
}

/**
 * Format EXIF data into compact settings string
 * Example output: "ISO 800, f/2.8, 1/500s, 35mm"
 *
 * @param exif - PhotoExifMetadata object
 * @returns Formatted settings string or null if all fields are missing
 */
export function formatExifCompact(exif: PhotoExifMetadata | null): string | null {
  if (!exif) return null;

  const parts: string[] = [];

  // ISO
  const iso = formatISO(exif);
  if (iso) parts.push(iso);

  // Aperture
  const aperture = formatAperture(exif);
  if (aperture) parts.push(aperture);

  // Shutter speed
  const shutterSpeed = formatShutterSpeed(exif);
  if (shutterSpeed) parts.push(shutterSpeed);

  // Focal length
  const focalLength = formatFocalLength(exif);
  if (focalLength) parts.push(focalLength);

  return parts.length > 0 ? parts.join(', ') : null;
}

// ============================================================================
// Type Guards & Converters
// ============================================================================

/**
 * Type guard to detect PhotoExifMetadata structure
 * @param data - Any EXIF data
 * @returns True if data matches PhotoExifMetadata structure
 */
export function isPhotoExifMetadata(data: any): data is PhotoExifMetadata {
  if (!data || typeof data !== 'object') return false;
  // PhotoExifMetadata has nested objects like exposure, focus, camera, lens
  return (
    'exposure' in data ||
    'focus' in data ||
    'camera' in data ||
    'lens' in data ||
    'capture' in data ||
    'settings' in data
  );
}

/**
 * Type guard to detect legacy ExifData structure
 * @param data - Any EXIF data
 * @returns True if data matches legacy ExifData structure
 */
export function isLegacyExifData(data: any): data is ExifData {
  if (!data || typeof data !== 'object') return false;
  // Legacy ExifData has flat properties like ISO, FocalLength, FNumber
  return (
    'ISO' in data ||
    'FocalLength' in data ||
    'FNumber' in data ||
    'ExposureTime' in data ||
    'Make' in data ||
    'Model' in data
  );
}

/**
 * Normalize any EXIF data format to PhotoExifMetadata structure
 * Converts legacy flat format to nested format
 *
 * @param data - EXIF data in any format
 * @returns Normalized PhotoExifMetadata or null if data is invalid
 */
export function normalizeExifData(data: any): PhotoExifMetadata | null {
  if (!data) return null;

  // Already in PhotoExifMetadata format
  if (isPhotoExifMetadata(data)) {
    return data as PhotoExifMetadata;
  }

  // Convert legacy ExifData format to PhotoExifMetadata
  if (isLegacyExifData(data)) {
    const legacy = data as ExifData;
    const normalized: PhotoExifMetadata = {};

    // Camera
    if (legacy.Make || legacy.Model) {
      normalized.camera = {
        make: legacy.Make,
        model: legacy.Model,
      };
    }

    // Lens
    if (legacy.LensModel) {
      normalized.lens = {
        model: legacy.LensModel,
      };
    }

    // Exposure
    if (legacy.ISO || legacy.FNumber || legacy.ExposureTime) {
      normalized.exposure = {
        iso: legacy.ISO,
        fNumber: legacy.FNumber ? parseFloat(legacy.FNumber.replace('f/', '')) : undefined,
        exposureTime: legacy.ExposureTime,
      };
    }

    // Focus
    if (legacy.FocalLength) {
      normalized.focus = {
        focalLength: parseFloat(legacy.FocalLength.replace('mm', '')),
      };
    }

    // Capture
    if (legacy.DateTimeOriginal) {
      normalized.capture = {
        dateTimeOriginal: legacy.DateTimeOriginal,
      };
    }

    return normalized;
  }

  // Unknown format
  return null;
}

// ============================================================================
// Display Helpers
// ============================================================================

/**
 * Get EXIF data formatted for grid display
 * Returns array of label-value pairs
 *
 * @param exif - PhotoExifMetadata object
 * @returns Array of {label, value} objects for 2-column grid
 */
export function getExifGridData(
  exif: PhotoExifMetadata | null
): Array<{ label: string; value: string }> {
  if (!exif) return [];

  const rows: Array<{ label: string; value: string }> = [];

  // Camera
  const camera = formatCamera(exif);
  if (camera) rows.push({ label: 'Camera', value: camera });

  // Lens - with smart prefix removal
  const lens = formatLens(exif, true);
  if (lens) rows.push({ label: 'Lens', value: lens });

  // ISO
  const iso = formatISO(exif);
  if (iso) rows.push({ label: 'ISO', value: iso });

  // Aperture
  const aperture = formatAperture(exif);
  if (aperture) rows.push({ label: 'Aperture', value: aperture });

  // Shutter Speed
  const shutterSpeed = formatShutterSpeed(exif);
  if (shutterSpeed) rows.push({ label: 'Shutter', value: shutterSpeed });

  // Focal Length
  const focalLength = formatFocalLength(exif);
  if (focalLength) rows.push({ label: 'Focal Length', value: focalLength });

  // Capture Date
  const captureDate = formatCaptureDateTime(exif);
  if (captureDate) rows.push({ label: 'Captured', value: captureDate });

  // Location
  const location = formatLocation(exif);
  if (location) rows.push({ label: 'Location', value: location });

  return rows;
}

/**
 * Truncate artist name to 50 characters
 * @param artist - Artist name
 * @returns Truncated string or null if missing
 */
export function truncateArtist(artist?: string): string | null {
  if (!artist) return null;
  return artist.length > 50 ? `${artist.substring(0, 47)}...` : artist;
}

/**
 * Truncate copyright to 80 characters
 * @param copyright - Copyright string
 * @returns Truncated string or null if missing
 */
export function truncateCopyright(copyright?: string): string | null {
  if (!copyright) return null;
  return copyright.length > 80 ? `${copyright.substring(0, 77)}...` : copyright;
}
